import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgTerminal } from 'ng-terminal';
import { WebLinksAddon } from 'xterm-addon-web-links';
import { AuthGuard } from 'src/app/services/auth.guard';
import { IdentityService } from 'src/app/services/identity.service';
import { NotificationService } from 'src/app/services/notification.service';
import { SocketService } from 'src/app/services/socket.service';

@Component({
    selector: 'app-terminal',
    encapsulation: ViewEncapsulation.None,
    templateUrl: './terminal.component.html',
    styleUrls: ['./terminal.component.scss'],
    standalone: false
})
export class TerminalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('child', { static: false }) child: NgTerminal;
  @Input() command: string = null;
  @Input() readOnly: boolean = false;
  @Input() teamName: string;
  @Input() disconnectEvent: boolean = false;
  @Input() isWidget: boolean = false;
  @Input() terminalId: string = null;
  @Input() disconnectOnDestroy: boolean = false;
  /** A terminal that mirrors a screen of a known size (the bridge paints a
   *  200×50 grid and never resizes). The font is scaled down until the panel
   *  holds AT LEAST this grid, so the stream never wraps — what the desktop
   *  app does. The terminal itself still fills the panel (ng-terminal re-fits
   *  on every layout change, so an exact grid cannot be held through it).
   *  null = the normal font size, whatever grid that gives. */
  @Input() minGrid: { cols: number; rows: number } | null = null;

  private static readonly FONT_FAMILY = '"Cascadia Code", Menlo, monospace';
  private static readonly FONT_SIZE = 13;
  private static readonly MIN_GRID_FONT_SIZE = 5;
  private _fontSize = TerminalComponent.FONT_SIZE;
  private _lastScreen: { cols: number; rows: number } | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _cellProbe: HTMLElement | null = null;

  @Output() disconnected = new EventEmitter();

  private subscription: any;
  private commandExecuted: boolean = false;

  public isLoading: boolean = true;

  @HostListener('window:beforeunload', ['$event'])
  unloadHandler(event: Event) {
    this.disconnect();
  }

  constructor(
    private activatedRoute: ActivatedRoute,
    private socketService: SocketService,
    private identity: IdentityService,
    private authGuard: AuthGuard,
    private notificationService: NotificationService,
    private hostRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    (<any>window).terminal = this;
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', () => this.onResize());
    this._resizeObserver?.disconnect();
    this._cellProbe?.remove();
    if (this.disconnectOnDestroy) this.disconnect();
  }

  onResize() {
    if (this.minGrid) { this.fitMinGrid(); return; }
    this.socketService.emit('resize', this.getScreen());
  }

  /** Width and height of one character cell at a font size. */
  private measureCell(fontSize: number): { w: number; h: number } {
    if (!this._cellProbe) {
      const el = document.createElement('span');
      el.style.cssText = 'position:absolute;visibility:hidden;white-space:pre;left:-9999px;top:0;';
      el.style.fontFamily = TerminalComponent.FONT_FAMILY;
      el.textContent = 'W'.repeat(40);
      document.body.appendChild(el);
      this._cellProbe = el;
    }
    this._cellProbe.style.fontSize = `${fontSize}px`;
    const r = this._cellProbe.getBoundingClientRect();
    return { w: r.width / 40, h: r.height };
  }

  /** minGrid: the largest font (up to the normal size) at which the panel
   *  holds the whole grid; then tell the remote PTY the size xterm ended up at. */
  private fitMinGrid(): void {
    if (!this.minGrid || !this.child) return;
    const host = this.hostRef.nativeElement;
    const width = host.clientWidth - 30;   // scrollbar + ng-terminal's padding
    const height = host.clientHeight - 12;
    if (width <= 0 || height <= 0) return;
    let size = TerminalComponent.MIN_GRID_FONT_SIZE;
    for (let s = TerminalComponent.FONT_SIZE; s >= TerminalComponent.MIN_GRID_FONT_SIZE; s -= 0.5) {
      const cell = this.measureCell(s);
      if (Math.floor(width / cell.w) >= this.minGrid.cols && Math.floor(height / Math.ceil(cell.h)) >= this.minGrid.rows) {
        size = s;
        break;
      }
    }
    if (size !== this._fontSize) {
      this._fontSize = size;
      this.child.setXtermOptions({ fontFamily: TerminalComponent.FONT_FAMILY, fontSize: size, cursorBlink: true });
    }
    // ng-terminal re-fits xterm to the panel on its own; report the result once it has.
    setTimeout(() => {
      const screen = this.getScreen();
      if (screen.cols === this._lastScreen?.cols && screen.rows === this._lastScreen?.rows) return;
      this._lastScreen = screen;
      this.socketService.emit('resize', screen);
    }, 60);
  }

  ngAfterViewInit(): void {
    this.child.setXtermOptions({
      fontFamily: TerminalComponent.FONT_FAMILY,
      fontSize: TerminalComponent.FONT_SIZE,
      cursorBlink: true
    });
    if (this.minGrid) {
      this.fitMinGrid();
      // Panel splitters resize the host without a window resize.
      this._resizeObserver = new ResizeObserver(() => this.fitMinGrid());
      this._resizeObserver.observe(this.hostRef.nativeElement);
    }

    // Enable clickable URLs in the terminal
    const webLinksAddon = new WebLinksAddon((event, uri) => {
      // Open link in new tab when clicked
      console.log('Terminal link clicked:', uri);
      window.open(uri, '_blank');
    }, {
      // Enable hover callback for debugging
      hover: (event, text, range) => {
        console.log('Terminal link hover:', text, range);
      }
    });
    this.child.underlying.loadAddon(webLinksAddon);
    console.log('WebLinksAddon loaded');

    // Handle Ctrl+Shift+C for copy and Ctrl+Shift+V for paste
    // Use capturing phase to intercept before browser handles it
    const terminalElement = this.child.underlying.element;
    if (terminalElement) {
      terminalElement.addEventListener('keydown', (event: KeyboardEvent) => {
        // Ctrl+Shift+C - Copy selected text
        if (event.ctrlKey && event.shiftKey && (event.key === 'C' || event.key === 'c')) {
          event.preventDefault();
          event.stopPropagation();
          const selection = this.child.underlying.getSelection();
          if (selection) {
            // Focus the terminal first to ensure clipboard access
            this.child.underlying.focus();

            // Helper function for fallback copy (needed for HTTP/insecure contexts)
            const fallbackCopy = () => {
              const textArea = document.createElement('textarea');
              textArea.value = selection;
              textArea.style.position = 'fixed';
              textArea.style.opacity = '0';
              document.body.appendChild(textArea);
              textArea.focus();
              textArea.select();
              try {
                document.execCommand('copy');
                console.log('Copied via fallback:', selection.substring(0, 50) + (selection.length > 50 ? '...' : ''));
              } catch (e) {
                console.error('Fallback copy failed:', e);
              }
              document.body.removeChild(textArea);
              this.child.underlying.focus();
            };

            // Check if Clipboard API is available (requires HTTPS/secure context)
            if (navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(selection).then(() => {
                console.log('Copied to clipboard:', selection.substring(0, 50) + (selection.length > 50 ? '...' : ''));
              }).catch(err => {
                console.warn('Clipboard API failed, trying fallback:', err);
                fallbackCopy();
              });
            } else {
              // Clipboard API not available (HTTP context), use fallback directly
              console.log('Clipboard API not available, using fallback');
              fallbackCopy();
            }
          } else {
            console.log('No text selected in terminal');
          }
        }
        // Ctrl+Shift+V - Paste from clipboard
        if (event.ctrlKey && event.shiftKey && (event.key === 'V' || event.key === 'v')) {
          event.preventDefault();
          event.stopPropagation();
          this.child.underlying.focus();
          // Check if Clipboard API is available (requires HTTPS/secure context)
          if (navigator.clipboard && navigator.clipboard.readText) {
            navigator.clipboard.readText().then(text => {
              if (text) {
                this.socketService.emit('ssh', { terminalId: this.terminalId, msg: text });
                console.log('Pasted from clipboard:', text.substring(0, 50) + (text.length > 50 ? '...' : ''));
              }
            }).catch(err => {
              console.error('Failed to paste:', err);
            });
          } else {
            // Clipboard API not available - paste requires secure context
            console.warn('Paste requires HTTPS. Use right-click paste or access via HTTPS.');
            // Show user-visible notification
            this.notificationService.showError('Paste requires HTTPS. Use right-click paste instead.');
          }
        }
      }, true); // Use capture phase
    }

    window.addEventListener('resize', () => this.onResize());

    if (this.command || this.isWidget) {
      this.load(this.teamName);
    } else {
      this.activatedRoute.params.subscribe((params) => {
        this.load(params.team);
      });
    }
  }

  public async load(teamName: string): Promise<void> {
    this.teamName = teamName;

    this.isLoading = false;
    if (!this.readOnly) this.subscribeToTerminal();

    setTimeout(() => {
      this.connect();
      this.child.underlying.focus();
    }, 100); // bugfix: getScreen will return NaN if the view hasn't rendered completely
  }

  public subscribeToTerminal(): void {
    // Use onData() instead of onKey() to capture all input including paste
    this.child.onData().subscribe((data) => {
      this.socketService.emit('ssh', { terminalId: this.terminalId, msg: data });
    });
  }

  public subscribeToSocketEvents(config: any): void {
    this.socketService.sshConnect(config);
    this.subscription = this.socketService.sshEvents.subscribe((response: any) => {
      if (response.terminalId !== this.terminalId) return;

      let msg = response.msg;
      this.child.write(msg);
      if ((this.disconnectEvent || this.isWidget) && msg.trim() === '*** SSH CONNECTION CLOSED ***') this.disconnect();
      if (this.command && !this.commandExecuted && msg.endsWith('$ ')) {
        this.commandExecuted = true;
        this.socketService.emit('ssh', { terminalId: this.terminalId, msg: this.command + '\r' });
      }
    });
  }

  public disconnect(): void {
    if (!this.subscription) return;

    this.socketService.sshDisconnect();
    this.subscription.unsubscribe();
    this.subscription = null;
    if (this.disconnectEvent) this.child.underlying.dispose();
    else this.child.underlying.reset();
    this.disconnected.emit();
  }

  public async connect(): Promise<void> {
    // Check for local mode - skip auth token requirement
    const skipAuth = window['env']?.['skipAuth'] || window['env']?.['localMode'];

    let token = localStorage.getItem('id_token');
    if (!token && !skipAuth) {
      this.authGuard.logout();
      return;
    }

    // In local mode without a real token, use a placeholder
    if (!token && skipAuth) {
      token = 'local-mode';
    }

    const socketConfig = { teamName: this.teamName, username: this.identity.me.username, token: token, terminalId: this.terminalId };
    const screenInfo = this.getScreen();
    socketConfig['screen'] = screenInfo;
    this.subscribeToSocketEvents(socketConfig);
  }

  public getScreen(): { cols: number; rows: number } {
    // minGrid: the font varies, so ask xterm for the grid it actually has.
    if (this.minGrid && this.child?.underlying) {
      return { cols: this.child.underlying.cols, rows: this.child.underlying.rows };
    }
    const terminalElement = document.querySelector('ng-terminal');
    if (!terminalElement) {
      return { cols: 80, rows: 24 }; // Default values if element is not found
    }

    const width = terminalElement.clientWidth;
    const height = terminalElement.clientHeight;

    // Create a hidden element to measure character dimensions
    let measureElement = document.getElementById('measure-element');
    if (!measureElement) {
      measureElement = document.createElement('div');
      measureElement.id = 'measure-element';
      measureElement.style.visibility = 'hidden';
      measureElement.style.position = 'absolute';
      measureElement.style.whiteSpace = 'pre'; // Ensure no wrapping
      measureElement.textContent = 'M'; // Use a wide character like 'M' for measurement
      document.body.appendChild(measureElement);
    }

    // Apply the same font settings as your terminal
    const fontFamily = '"Cascadia Code", Menlo, monospace';
    const fontSize = 13;
    measureElement.style.fontFamily = fontFamily;
    measureElement.style.fontSize = `${fontSize}px`;

    // Measure the dimensions of a single character
    const charWidth = measureElement.clientWidth;
    const charHeight = measureElement.clientHeight;

    const cols = Math.floor(width / charWidth);
    const rows = Math.floor(height / charHeight);

    return { cols, rows };
  }

}
